<script setup lang="ts">
import { Fold, StarFilled } from "@element-plus/icons-vue";
import { Slotted } from "~/types/Slotted";
import { ContactActivityDto, WorkflowStatus } from "~/api";
import { formatDate } from "~/utils/dateUtils";
import EnumTag from "~/components/EnumTag.vue";
import { splitByCaps } from "~/utils/miscUtils";

const props = defineProps<{
  /** The contact activity rows to display */
  data: ContactActivityDto[];
  /** Status list for label resolution */
  statusList: WorkflowStatus[];
  /** Activity type options for label resolution */
  activityTypeOptions?: { label: string; value: string; disabled?: boolean }[];
  /** Whether to show the loading spinner */
  loading?: boolean;
  /** Whether to show the linked housing application column */
  showHousingApplicationColumn?: boolean;
}>();

const emit = defineEmits<{
  (e: "activity-click", activity: ContactActivityDto): void;
}>();

const getActivityTypeLabel = (type: string | null | undefined) => {
  if (!type) return "Incomplete Activity";
  return (
    props.activityTypeOptions?.find((o) => o.value === type)?.label ?? type
  );
};

const getStatusLabel = (statusCode: string) => {
  return (
    props.statusList?.find((s) => s.code === statusCode)?.label ?? statusCode
  );
};
</script>

<template>
  <el-table
    v-loading="loading ?? false"
    :data="data"
    style="width: 100%"
    sortable
    aria-label="Contact activities table"
  >
    <template #empty>
      <div class="empty-prompt">
        <p>No contact activities found matching your filters.</p>
      </div>
    </template>

    <!-- 1. Type — clickable, opens activity details -->
    <el-table-column label="Type" width="250">
      <template #default="{ row }: Slotted<ContactActivityDto>">
        <el-link
          type="primary"
          :icon="Fold"
          underline="never"
          :aria-label="`View ${getActivityTypeLabel(row.interactionType)} for ${row.contactName ?? row.workflowName ?? 'unknown contact'}, ${formatDate(row.interactionDate ?? row.createdAt)}`"
          @click="emit('activity-click', row)"
        >
          {{ getActivityTypeLabel(row.interactionType) }}
        </el-link>
      </template>
    </el-table-column>

    <!-- 2. Contact Name — plain text (future: opens contact drawer) -->
    <el-table-column label="Contact Name" width="150">
      <template #default="{ row }: Slotted<ContactActivityDto>">
        {{ row.contactName ?? row.workflowName }}
      </template>
    </el-table-column>

    <!-- 3. Housing App -->
    <el-table-column
      v-if="showHousingApplicationColumn"
      label="Housing App"
      width="150"
    >
      <template #default="{ row }: Slotted<ContactActivityDto>">
        {{ row.linkedHousingApplicationName ?? "-" }}
      </template>
    </el-table-column>

    <!-- 4. App Status — current housing application status -->
    <el-table-column
      v-if="showHousingApplicationColumn"
      label="App Status"
      width="170"
      align="center"
    >
      <template #default="{ row }: Slotted<ContactActivityDto>">
        <EnumTag
          v-if="row.linkedHousingApplicationCurrentStatus"
          :model-value="row.linkedHousingApplicationCurrentStatus"
          type="housingApplicationStatus"
        />
        <template v-else>-</template>
      </template>
    </el-table-column>

    <!-- 5. Overall App Status at Create — snapshot at activity creation -->
    <el-table-column label="Status at Create" width="170" align="center">
      <template #default="{ row }: Slotted<ContactActivityDto>">
        <EnumTag
          v-if="row.statusOnCreate"
          :model-value="row.statusOnCreate"
          type="housingApplicationStatus"
        />
        <template v-else>-</template>
      </template>
    </el-table-column>

    <!-- 6. Contact Reason -->
    <el-table-column label="Contact Reason" width="170">
      <template #default="{ row }: Slotted<ContactActivityDto>">
        {{ splitByCaps(row.reason) ?? "-" }}
      </template>
    </el-table-column>

    <!-- 7. Important -->
    <el-table-column label="Important" width="150" align="center">
      <template #default="{ row }: Slotted<ContactActivityDto>">
        <el-icon v-if="row.isImportant" color="var(--el-color-warning)">
          <StarFilled />
        </el-icon>
        <template v-else>-</template>
      </template>
    </el-table-column>

    <!-- 8. Activity Date — interactionDate, fallback to createdAt -->
    <el-table-column label="Activity Date" width="150">
      <template #default="{ row }: Slotted<ContactActivityDto>">
        <el-tooltip
          :content="formatDate(row.interactionDate ?? row.createdAt, 1)"
          placement="top"
        >
          <span>{{ formatDate(row.interactionDate ?? row.createdAt) }}</span>
        </el-tooltip>
      </template>
    </el-table-column>

    <!-- 9. Assigned To -->
    <el-table-column label="Assigned To" width="150">
      <template #default="{ row }: Slotted<ContactActivityDto>">
        {{ row.assignedToUserName ?? "-" }}
      </template>
    </el-table-column>

    <!-- 10. Due Date -->
    <el-table-column label="Due Date" width="130">
      <template #default="{ row }: Slotted<ContactActivityDto>">
        <el-tooltip
          v-if="row.dueDate"
          :content="formatDate(row.dueDate, 1)"
          placement="top"
        >
          <span>{{ formatDate(row.dueDate) }}</span>
        </el-tooltip>
        <template v-else>-</template>
      </template>
    </el-table-column>

    <!-- 11. Result -->
    <el-table-column label="Result" width="140">
      <template #default="{ row }: Slotted<ContactActivityDto>">
        {{ splitByCaps(row.result) ?? "-" }}
      </template>
    </el-table-column>

    <!-- 12. Activity Status -->
    <el-table-column label="Activity Status" width="190" align="center">
      <template #default="{ row }: Slotted<ContactActivityDto>">
        <el-tag type="info">
          {{ getStatusLabel(row.workflowStatus) }}
        </el-tag>
      </template>
    </el-table-column>

    <!-- 13. Created Date — date only -->
    <el-table-column label="Created Date" width="140">
      <template #default="{ row }: Slotted<ContactActivityDto>">
        <el-tooltip :content="formatDate(row.createdAt, 1)" placement="top">
          <span>{{ formatDate(row.createdAt) }}</span>
        </el-tooltip>
      </template>
    </el-table-column>

    <!-- 14. Created By -->
    <el-table-column label="Created By" width="150">
      <template #default="{ row }: Slotted<ContactActivityDto>">
        {{ row.createdByUserName ?? "-" }}
      </template>
    </el-table-column>
  </el-table>
</template>
